import { useTranslation } from 'react-i18next';
import { useMetadata } from '@/hooks/useMetadata';

/**
 * OrderRewardBanner Component
 * Displays a banner with order reward information if orderRewardTips exists in metadata
 * 
 * @param {Object} props
 * @param {Object} [props.orderRewardTips] - Optional orderRewardTips data. If not provided, will fetch from metadata hook.
 * @param {number} [props.imageLeftPadding] - Optional left padding for image. Default: 16px (pl-4)
 * @param {number} [props.imageTextGap] - Optional gap between image and text. Default: 4px (ml-1)
 * @returns {JSX.Element|null} The banner component or null if no orderRewardTips
 */
const OrderRewardBanner = ({ 
  orderRewardTips: orderRewardTipsProp,
  imageLeftPadding = 16,
  imageTextGap = 4
}) => {
  const { t } = useTranslation();
  // Use prop if provided, otherwise fetch from metadata hook (for backward compatibility)
  const metadataQuery = useMetadata();
  const orderRewardTips = orderRewardTipsProp || metadataQuery?.data?.orderRewardTips;
  
  // Don't show component if loading (only when using hook and no prop provided) or orderRewardTips is empty/null
  if ((!orderRewardTipsProp && metadataQuery.isLoading) || !orderRewardTips || !orderRewardTips.icon) {
    return null;
  }

  const iconUrl = orderRewardTips.icon.url;
  // Title is at orderRewardTips.title, not icon.title
  const iconTitle = orderRewardTips.title || '';

  // If no iconUrl, don't show the component
  if (!iconUrl) {
    return null;
  }

  // Get Tailwind classes based on padding values
  const imageLeftPaddingClass = imageLeftPadding === 0 ? 'pl-0' : imageLeftPadding === 16 ? 'pl-4' : `pl-[${imageLeftPadding}px]`;
  const imageTextGapClass = imageTextGap === 3 ? 'ml-[3px]' : imageTextGap === 4 ? 'ml-1' : `ml-[${imageTextGap}px]`;

  return (
    <div className="w-full h-[54px] bg-[#FDEAE2] flex items-center relative pr-3 rounded-[6px]">
      {/* Image - auto width x 60px height, bottom aligned (image will extend above the banner) */}
      <div className={`self-end flex-shrink-0 ${imageLeftPaddingClass}`}>
        <img 
          src={iconUrl} 
          alt={iconTitle}
          className="w-auto h-[60px] object-contain"
        />
      </div>
      
      {/* Text content - margin from image, vertically centered in banner */}
      <div className={`${imageTextGapClass} flex items-center h-[54px] flex-1 min-w-0`}>
        <span className="font-['Noto_Sans_JP'] text-sm leading-[150%] text-[#FF5601] break-words">
          {t('orderReward.prefix')}
          <span className="font-bold">「{iconTitle}」</span>
          {t('orderReward.suffix')}
        </span>
      </div>
    </div>
  );
};

export default OrderRewardBanner;

